/**
 * Az edző részleges jelenlétének kliens-oldali szabályai — a backend `CoachPresence` tükre.
 * Idők 'HH:mm' vagy 'HH:mm:ss' formában (az API 'HH:mm:ss'-t ad és fogad).
 * Klub-döntés (2026-09-11): mindent fél órára kerekítünk; az ablak az esemény idején belül van.
 */

export const STEP_MINUTES = 30;

export interface PresenceWindow {
  presentFrom: string;  // 'HH:mm:ss'
  presentUntil: string; // 'HH:mm:ss'
}

export interface QuickOption {
  label: string;
  presentFrom: string;
  presentUntil: string;
}

const pad = (n: number) => String(n).padStart(2, '0');

export const toMinutes = (time: string): number => {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + (m || 0);
};

export const toTime = (minutes: number): string => `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}:00`;

/** 'HH:mm:ss' → 'HH:mm' a megjelenítéshez. */
export const hhmm = (time: string): string => time.slice(0, 5);

export const hoursBetween = (from: string, until: string): number => (toMinutes(until) - toMinutes(from)) / 60;

/** Az esemény teljes hossza órában. */
export const eventHoursOf = (eventStart: string, eventEnd: string): number => hoursBetween(eventStart, eventEnd);

/**
 * Magyar óraszám: 1,0 · 1,5 · 2,0 — a projekt EGYETLEN óraszám-formázója a weben is.
 * A mobil `utils/coachPresence.ts` párja, szándékosan BETŰRE ugyanaz az implementáció
 * (`toFixed` + vesszőre csere), nem `toLocaleString('hu-HU')`: az Intl a locale-adatok
 * nélkül fordított Node-on („small-icu") csendben en-US-re esik vissza és pontot ad,
 * a jsdom-os Jest-futásban pedig épp az alakot vizsgáljuk. Determinisztikus alak kell.
 */
export const formatHours = (hours: number): string => hours.toFixed(1).replace('.', ',');

/** A legközelebbi félórára kerekít (:00 / :30). */
export const snapToHalfHour = (time: string): string => {
  const minutes = Math.round(toMinutes(time) / STEP_MINUTES) * STEP_MINUTES;
  return toTime(Math.min(minutes, 23 * 60 + 30));
};

const halfHoursLabel = (halfHours: number): string => {
  if (halfHours === 1) return 'fél óra';
  if (halfHours === 2) return 'óra';
  return `${formatHours(halfHours / 2)} óra`;
};

/**
 * Gyors-chipek az esemény hosszából: „Első óra", „Első 1,5 óra", … (minden legalább egyórás kezdő
 * szakasz) és „Utolsó óra", „Utolsó 2,0 óra", … (egész órás záró szakaszok). Egyórás eseményre
 * nincs chip — ott csak az egyéni ablak marad.
 */
export const quickOptions = (eventStart: string, eventEnd: string): QuickOption[] => {
  const start = toMinutes(eventStart);
  const end = toMinutes(eventEnd);
  const totalHalfHours = Math.floor((end - start) / STEP_MINUTES);
  const options: QuickOption[] = [];
  for (let k = 2; k < totalHalfHours; k++) {
    options.push({ label: `Első ${halfHoursLabel(k)}`, presentFrom: toTime(start), presentUntil: toTime(start + k * STEP_MINUTES) });
  }
  for (let k = 2; k < totalHalfHours; k += 2) {
    options.push({ label: `Utolsó ${halfHoursLabel(k)}`, presentFrom: toTime(end - k * STEP_MINUTES), presentUntil: toTime(end) });
  }
  return options;
};

export const isSameWindow = (a: PresenceWindow | null | undefined, b: PresenceWindow | null | undefined): boolean =>
  (a?.presentFrom ?? null) === (b?.presentFrom ?? null) && (a?.presentUntil ?? null) === (b?.presentUntil ?? null);

/** Null = rendben; különben a felhasználónak szóló hibaüzenet (a szerver ugyanezt ellenőrzi). */
export const validateWindow = (eventStart: string, eventEnd: string, window: PresenceWindow): string | null => {
  const from = toMinutes(window.presentFrom);
  const until = toMinutes(window.presentUntil);
  if (from % STEP_MINUTES !== 0 || until % STEP_MINUTES !== 0) return 'Csak félórás lépésekben adható meg (:00 vagy :30).';
  if (from < toMinutes(eventStart) || until > toMinutes(eventEnd)) {
    return `A jelenlétnek az esemény idején belül kell lennie (${hhmm(eventStart)}–${hhmm(eventEnd)}).`;
  }
  if (until <= from) return 'A befejezésnek a kezdés után kell lennie.';
  if (until - from < STEP_MINUTES) return 'A jelenlét legalább fél óra.';
  return null;
};

/** Alapértelmezett ablak a „Részben" bekapcsolásakor: az első gyors-chip, ha van; különben az első fél óra. */
export const defaultWindow = (eventStart: string, eventEnd: string): PresenceWindow => {
  const first = quickOptions(eventStart, eventEnd)[0];
  if (first) return { presentFrom: first.presentFrom, presentUntil: first.presentUntil };
  const start = toMinutes(eventStart);
  return { presentFrom: toTime(start), presentUntil: toTime(Math.min(start + STEP_MINUTES, toMinutes(eventEnd))) };
};
